import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Loader2, RefreshCw, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { reflectQuestion, reflectAnswer } from "../lib/api";
import { ShareCardButton } from "./ShareCardButton";

export const DeepQuestion = ({ journeyId, fs }) => {
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [reply, setReply] = useState("");
  const [loadingQ, setLoadingQ] = useState(true);
  const [sending, setSending] = useState(false);

  const newQuestion = useCallback(async () => {
    setLoadingQ(true);
    setReply("");
    setAnswer("");
    try {
      const d = await reflectQuestion(journeyId);
      setQuestion(d.question);
    } finally {
      setLoadingQ(false);
    }
  }, [journeyId]);

  useEffect(() => { newQuestion(); }, [newQuestion]);

  const submit = async () => {
    if (!answer.trim() || sending) return;
    setSending(true);
    fs.ensureAged();
    try {
      const d = await reflectAnswer(journeyId, question, answer.trim(), fs.years);
      setReply(d.reply);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="deep-question">
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono-label text-white/50">A question for you</div>
        <button onClick={newQuestion} className="text-[11px] text-white/40 hover:text-white/80 flex items-center gap-1" data-testid="deep-question-refresh">
          <RefreshCw size={11} /> New question
        </button>
      </div>
      <h2 className="text-base md:text-lg font-display font-light mb-4 leading-relaxed text-white/90" data-testid="deep-question-text">
        {loadingQ ? <span className="text-white/40 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Finding a question…</span> : question}
      </h2>
      {!reply && !loadingQ && (
        <div className="flex flex-col gap-2">
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            rows={3}
            placeholder="Answer honestly…"
            data-testid="deep-question-answer"
            className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-300/60"
          />
          <button onClick={submit} disabled={sending} className="btn-pill btn-pill-gold justify-center text-sm" data-testid="deep-question-submit">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Hear from future you
          </button>
        </div>
      )}
      <AnimatePresence>
        {reply && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border border-amber-300/30 bg-amber-300/[0.04] p-4"
            data-testid="deep-question-reply"
          >
            <div className="font-mono-label text-amber-300/80 mb-2">Future you at {fs.futureAgeLabel().replace("Age ", "")} says</div>
            <p className="text-sm italic text-amber-100/90 leading-relaxed">{reply}</p>
            <div className="flex gap-2 flex-wrap mt-4">
              <ShareCardButton fs={fs} message={reply} testId="deep-question-share" />
              <button onClick={() => navigate(`/journal/${journeyId}`)} className="btn-pill text-xs" data-testid="deep-question-journal">
                <BookOpen size={12} /> Saved to journal
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
